import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Chip } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { getFirstName } from '../../../../services/authService';
import { ChatSuggestions } from '../chatConfig';
import ChatMessage from './chatMessage';

const TypingIndicator = () => (
	<Box className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-gray-100 px-3 py-3 w-fit">
		{[0, 1, 2].map((dot) => (
			<Box
				key={dot}
				className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400"
				style={{ animationDelay: `${dot * 0.15}s` }}
			/>
		))}
	</Box>
);

ChatThread.propTypes = {
	messages: PropTypes.array.isRequired,
	isSending: PropTypes.bool,
	onRetry: PropTypes.func.isRequired,
	onSuggestion: PropTypes.func.isRequired,
};

export default function ChatThread({
	messages,
	isSending,
	onRetry,
	onSuggestion,
}) {
	const scrollRef = useRef(null);

	useEffect(() => {
		const node = scrollRef.current;
		if (node) {
			node.scrollTop = node.scrollHeight;
		}
	}, [messages, isSending]);

	return (
		<Box
			ref={scrollRef}
			className="flex-1 space-y-3 overflow-y-auto px-3 py-3"
			aria-live="polite">
			{messages.length === 0 ? (
				<Box className="flex h-full flex-col justify-center px-2">
					<AutoAwesomeIcon color="primary" />
					<Box className="mt-2 text-[18px] font-semibold text-gray-800">
						Hello{getFirstName() ? `, ${getFirstName()}` : ''}
					</Box>
					<Box className="text-[13px] text-gray-500">
						Ask a question about the platform and I will look it up.
					</Box>
					<Box className="mt-4 flex flex-wrap gap-2">
						{ChatSuggestions.map((suggestion) => (
							<Chip
								key={suggestion}
								label={suggestion}
								variant="outlined"
								clickable
								onClick={() => onSuggestion(suggestion)}
								sx={{
									height: 'auto',
									borderRadius: '14px',
									'& .MuiChip-label': {
										fontSize: '12px',
										whiteSpace: 'normal',
										padding: '6px 8px',
									},
								}}
							/>
						))}
					</Box>
				</Box>
			) : (
				messages.map((message) => (
					<ChatMessage
						key={message.id}
						message={message}
						isSending={isSending}
						onRetry={onRetry}
					/>
				))
			)}
			{isSending ? <TypingIndicator /> : null}
		</Box>
	);
}
